import { Alert, Badge, Button, List, Modal, Popover, Typography, message } from 'antd'
import { BellOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  confirmAnnouncement,
  getAnnouncementSummary,
  listInboxAnnouncements,
  subscribeAnnouncementChanges,
  type AnnouncementItem,
  type AnnouncementSummary,
} from '../../services/announcements'

const DISMISS_KEY = 'kyren-announcement-important-dismissed'

interface AnnouncementShellProps {
  children: (
    announcementHeader: ReactNode,
    urgentAnnouncementActive: boolean,
    onAgreementDialogsClosed: () => void,
  ) => ReactNode
}

export default function AnnouncementShell({ children }: AnnouncementShellProps) {
  const navigate = useNavigate()
  const [summary, setSummary] = useState<AnnouncementSummary>({ unreadCount: 0 })
  const [recent, setRecent] = useState<AnnouncementItem[]>([])
  const [confirming, setConfirming] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1')
  const [summaryPending, setSummaryPending] = useState(true)
  const [agreementDialogsClosed, setAgreementDialogsClosed] = useState(false)
  const [urgentModalVisible, setUrgentModalVisible] = useState(false)
  const pendingRefreshes = useRef(0)
  const refreshSequence = useRef(0)
  const latestAppliedRefresh = useRef(0)
  const urgentAnnouncementActiveRef = useRef(true)

  const refresh = useCallback(async () => {
    const requestSequence = ++refreshSequence.current
    pendingRefreshes.current += 1
    if (!urgentAnnouncementActiveRef.current) setAgreementDialogsClosed(false)
    setSummaryPending(true)
    try {
      const response = await getAnnouncementSummary()
      if (requestSequence >= latestAppliedRefresh.current) {
        latestAppliedRefresh.current = requestSequence
        setSummary(response.data)
      }
    } catch {
      // Announcement loading must not prevent normal merchant work.
    } finally {
      pendingRefreshes.current -= 1
      if (pendingRefreshes.current === 0) setSummaryPending(false)
    }
  }, [])

  const urgentAnnouncementRequested = Boolean(summary.urgentAnnouncement)
  const urgentAnnouncementActive = summaryPending || urgentModalVisible || urgentAnnouncementRequested
  const urgentModalOpen = urgentAnnouncementRequested && agreementDialogsClosed
  urgentAnnouncementActiveRef.current = urgentAnnouncementActive

  useEffect(() => {
    if (urgentModalOpen) setUrgentModalVisible(true)
  }, [urgentModalOpen])

  const handleAgreementDialogsClosed = useCallback(() => {
    setAgreementDialogsClosed(true)
  }, [])

  useEffect(() => {
    refresh()
    return subscribeAnnouncementChanges(refresh)
  }, [refresh])

  const loadRecent = async () => {
    try {
      const response = await listInboxAnnouncements({ page: 1, size: 5, unreadOnly: true })
      setRecent(response.data.items)
    } catch {
      setRecent([])
    }
  }

  const confirmUrgent = async () => {
    if (!summary.urgentAnnouncement) return
    setConfirming(true)
    try {
      await confirmAnnouncement(summary.urgentAnnouncement.id)
      await refresh()
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '確認失敗，請重試')
    } finally {
      setConfirming(false)
    }
  }

  const dismissBanner = () => {
    sessionStorage.setItem(DISMISS_KEY, '1')
    setBannerDismissed(true)
  }

  const announcementHeader = (
    <div className="announcement-header-slot">
      {summary.importantAnnouncement && !bannerDismissed && (
        <Alert
          className="announcement-important-inline"
          type="warning"
          showIcon
          closable
          message={(
            <Typography.Text className="announcement-important-title" title={summary.importantAnnouncement.title}>
              {summary.importantAnnouncement.title}
            </Typography.Text>
          )}
          action={(
            <Button type="link" size="small" onClick={() => navigate(`/dashboard/announcements?id=${summary.importantAnnouncement?.id}`)}>
              查看詳情
            </Button>
          )}
          onClose={dismissBanner}
        />
      )}
      <div className="announcement-bell-slot">
      <Popover
        trigger="click"
        placement="bottomRight"
        onOpenChange={(open) => open && loadRecent()}
        content={(
          <div className="announcement-popover">
            <List
              size="small"
              locale={{ emptyText: '沒有未讀公告' }}
              dataSource={recent}
              renderItem={(item) => (
                <List.Item>
                  <Button type="text" block className="announcement-row-button" onClick={() => navigate(`/dashboard/announcements?id=${item.id}`)}>{item.title}</Button>
                </List.Item>
              )}
            />
            <Button type="link" block onClick={() => navigate('/dashboard/announcements')}>查看全部公告</Button>
          </div>
        )}
      >
        <Badge count={summary.unreadCount} size="small">
          <Button type="text" shape="circle" icon={<BellOutlined />} aria-label={`系統公告，${summary.unreadCount} 則未讀`} />
        </Badge>
      </Popover>
      </div>
    </div>
  )

  return (
    <>
      {children(announcementHeader, urgentAnnouncementActive, handleAgreementDialogsClosed)}
      <Modal
        open={urgentModalOpen}
        afterClose={() => setUrgentModalVisible(false)}
        title="緊急系統公告"
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={<Button type="primary" loading={confirming} onClick={confirmUrgent}>我已閱讀並知悉</Button>}
      >
        <Typography.Title level={5}>{summary.urgentAnnouncement?.title}</Typography.Title>
        <Typography.Paragraph className="announcement-body">{summary.urgentAnnouncement?.content}</Typography.Paragraph>
      </Modal>
    </>
  )
}
